import { useEffect } from 'react';
import {
  readDevicePixelRatio,
  resolveZoom,
  shouldApplyDpiCompensatingZoom,
} from '../config/zoomBreakpoints';

function clearRootZoomStyles(root: HTMLElement) {
  root.style.removeProperty('zoom');
  root.style.removeProperty('--ipc-ui-zoom');
  root.style.removeProperty('height');
  root.style.removeProperty('min-height');
  root.style.removeProperty('width');
  root.style.removeProperty('min-width');
}

function readVisualViewportSize() {
  const vv = window.visualViewport;
  return {
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
  };
}

/**
 * zoom 后若仍用 height:100%，视觉偏矮留白；若用 100%/zoom 百分比，
 * 在 Chromium + overflow:hidden 下常与裁剪盒错位，底部被挡。
 * 改为：布局高度 = 可视视口高度 / zoom（px），缩放后刚好等于可视区。
 */
function applyRootZoomStyles(root: HTMLElement, zoom: number) {
  root.style.setProperty('--ipc-ui-zoom', String(zoom));
  // 不补 width，避免横向副作用；高度用可视视口像素补偿
  root.style.removeProperty('width');
  root.style.removeProperty('min-width');

  if (zoom === 1) {
    root.style.removeProperty('zoom');
    root.style.removeProperty('height');
    root.style.removeProperty('min-height');
    return;
  }

  const { height: visualH } = readVisualViewportSize();
  const layoutH = Math.max(1, Math.round(visualH / zoom));
  root.style.zoom = String(zoom);
  root.style.height = `${layoutH}px`;
  root.style.minHeight = `${layoutH}px`;
}

/**
 * Windows：按 devicePixelRatio 设置 html { zoom }，抵消系统显示缩放。
 * iOS / macOS：不处理，保持系统正常显示。
 */
export function useRootZoom(): void {
  useEffect(() => {
    const root = document.documentElement;
    clearRootZoomStyles(root);

    if (!shouldApplyDpiCompensatingZoom()) {
      return undefined;
    }

    let frame = 0;
    let lastZoom: number | null = null;
    let dprMql: MediaQueryList | null = null;

    const apply = () => {
      frame = 0;
      if (!shouldApplyDpiCompensatingZoom()) {
        lastZoom = null;
        clearRootZoomStyles(root);
        return;
      }
      const next = resolveZoom(readDevicePixelRatio());
      const zoomChanged = lastZoom !== next;
      lastZoom = next;
      // 视口变化时即使 zoom 不变也要重算 px 高度
      applyRootZoomStyles(root, next);
      if (zoomChanged) {
        window.dispatchEvent(new Event('resize'));
      }
    };

    const scheduleApply = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(apply);
    };

    const onDprChange = () => {
      dprMql?.removeEventListener('change', onDprChange);
      dprMql = window.matchMedia(`(resolution: ${readDevicePixelRatio()}dppx)`);
      dprMql.addEventListener('change', onDprChange);
      scheduleApply();
    };

    apply();
    dprMql = window.matchMedia(`(resolution: ${readDevicePixelRatio()}dppx)`);
    dprMql.addEventListener('change', onDprChange);
    window.addEventListener('resize', scheduleApply);
    window.visualViewport?.addEventListener('resize', scheduleApply);
    window.visualViewport?.addEventListener('scroll', scheduleApply);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      dprMql?.removeEventListener('change', onDprChange);
      window.removeEventListener('resize', scheduleApply);
      window.visualViewport?.removeEventListener('resize', scheduleApply);
      window.visualViewport?.removeEventListener('scroll', scheduleApply);
      clearRootZoomStyles(root);
    };
  }, []);
}
